import {
  Checkbox, Col, Divider, Flex, message, Row
} from 'antd';
import React, { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import {
  Button, Image, Radio, Typography
} from 'tfc-components';

import ChoosePackage from './ChoosePackage';

import {
  IBillingInfo, IBusinessInfo,
  InvoiceType, PackageForm
} from '.';

import backupCardImg from 'assets/images/backup_card.jpg';
import imgCard from 'assets/images/card_notice.png';
import masterCard from 'assets/images/master_card.png';
import nganLuongImg from 'assets/images/nganluong-logo-color.png';
import mainCardImg from 'assets/images/primary_card.jpg';
import visa from 'assets/images/visa.png';
import Modal from 'components/organisms/Modal';
import { addNewPackageService, getCardService } from 'services/overview';
import { AddNewPackageData, CardData } from 'services/overview/types';
import mapModifiers from 'utils/functions';

interface Props {
  total: number;
  duration: 'monthly' | 'yearly';
  billingType: 'one-time' | 'subscription';
  billingData: IBillingInfo;
  visiblePackages: {
    id: string; name: string, quantity: number,
    price: number, duration: number, packageId?: string
  }[];
  method: UseFormReturn<PackageForm>;
  discountData?: {
    discountAmount: number,
    discountDay: number,
    servicePackageAddOns: {
      discountAmount?: number;
      maxQty?: number;
      quantity: number;
      servicePackageAddOnId: number;
      unit: number;
    }[];
  } | null
}

const paymentMethodsSubscription = [
  'JCB',
  'Master Card',
  'Visa'
];

const paymentMethodsOnetime = [
  'Viet QR',
  'ATM Card',
  'Internet Banking',
  'E-Wallet',
  'JCB',
  'Master Card',
  'Visa'
];

const Confirmation: React.FC<Props> = ({
  total, duration, billingData, visiblePackages, billingType, method, discountData
}) => {
  const [checked, setChecked] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [usedCard, setUsedCard] = useState<'main' | 'backup'>('main');
  const [paymentMethod, setMethod] = useState<'card' | 'other'>('card');
  const [mainCard, setMainCard] = useState<CardData | null>(null);
  const [backupCard, setBackupCard] = useState<CardData | null>(null);

  const RETURN_URL: string = `${window.location.origin}/add-new-package/payment-status/success`;
  const CANCEL_URL: string = `${window.location.origin}/add-new-package/payment-status/cancel`;

  /* Queries */
  const { data: cardData, } = useQuery({
    queryKey: ['get-card'],
    queryFn: getCardService,
  });

  /* Mutation */
  const { mutateAsync: handleAddNew } = useMutation({
    mutationKey: ['add-new-package'],
    mutationFn: addNewPackageService,
    onError: (errors: ErrorResponse[]) => {
      if (errors.length > 0) {
        errors.forEach((error: ErrorResponse) => {
          message.error(error?.message || '');
        });
      } else {
        message.error('Something went wrong');
      }
    },
    onSuccess: (data) => {
      localStorage.setItem('paymentChecked', data.code);
      window.location.href = (data.link);
    }
  });

  const handleCheckout = async () => {
    if (!billingData) return;
    try {
      const checkoutDataFull: AddNewPackageData = {
        invoiceData: {
          invoiceType: billingData.invoiceType === ('business' as InvoiceType) ? 2 : 1,
          taxCode: billingData.invoiceType === ('business' as InvoiceType) ? (billingData.invoice as IBusinessInfo).taxId : (billingData.invoice as IBusinessInfo).taxId,
          companyName: billingData.invoiceType === ('business' as InvoiceType) ? (billingData.invoice as IBusinessInfo).companyName : (billingData.invoice as IBusinessInfo).companyName,
          fullAddress: billingData.invoice.fullAddress,
          invoiceEmails: billingData.invoice.invoiceEmail || [],
        },
        billingType: billingType === 'subscription' ? 2 : 1,
        returnUrl: RETURN_URL,
        cancelUrl: CANCEL_URL,
        cardTokenId: (() => {
          if (paymentMethod !== 'card') return null;
          if (usedCard === 'main') return mainCard?.cardInfoData?.id || 0;
          return backupCard?.cardInfoData?.id || 0;
        })(),
        servicePackageAddOns: visiblePackages.map((item) => ({
          servicePackageAddOnId: item?.packageId ? parseInt(item.packageId.replace(/^\D+/g, ''), 10) : 0,
          quantity: item?.duration,
          unit: item?.quantity
        }))
      };
      handleAddNew(checkoutDataFull);
      // checkoutMutate({ ...checkoutDataFull, ggRecaptchaToken: token });
    } catch (error) {
      message.error('Something went wrong, please try again later');
    }
  };

  useEffect(() => {
    const mainCardCandidate = cardData?.find(
      (card) => card?.cardInfoData?.cardIndex === 1
    );
    const backupCardCandidate = cardData?.find(
      (card) => card?.cardInfoData?.cardIndex === 2
    );

    if (mainCardCandidate) {
      setMainCard(mainCardCandidate);
      setBackupCard(backupCardCandidate ?? null);
    } else if (backupCardCandidate) {
      setMainCard(backupCardCandidate);
      setBackupCard(null);
    } else {
      setBackupCard(null);
      setMainCard(null);
    }

    if (cardData?.length === 0) {
      setMethod('other');
    } else {
      setMethod('card');
    }
  }, [cardData, cardData?.length]);

  return (
    <div className="p-addNewPackage_confirmation">
      <div className="p-addNewPackage_confirmation_wrap">
        <ChoosePackage
          billingType={billingType}
          total={total}
          duration={duration}
          readonly
          visiblePackages={visiblePackages}
          width="158px"
          method={method}
          isConfirm
          discountData={discountData}
        />
        <div className="p-addNewPackage_spacingBottom">
          <Typography.Text fontweight="600" extendClasses="p-addNewPackage_titleProduct p-addNewPackage_billingInfo_spacing fs-20x24">
            Invoice Information
          </Typography.Text>
          <div className="p-addNewPackage_buyerInfo">
            <Row>
              <Col className="p-addNewPackage_buyerInfo_col">
                <Typography.Text fontweight="600" extendClasses="p-addNewPackage_billingType_formLabel p-home_demo_formLabel fs-14x18">Invoice Recipient Type</Typography.Text>
                <Typography.Text extendClasses="p-addNewPackage_text">
                  {billingData.invoiceType.toLowerCase()
                    .split(/\s+/)
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')}
                </Typography.Text>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col className="p-addNewPackage_buyerInfo_col">
                <Typography.Text fontweight="600" extendClasses="p-addNewPackage_billingType_formLabel p-home_demo_formLabel fs-14x18">
                  {billingData.invoiceType === 'business'
                    ? 'Tax ID' : 'National ID'}
                </Typography.Text>
                <Typography.Text extendClasses="p-addNewPackage_text">
                  {billingData.invoiceType === 'business' ? (billingData.invoice as IBusinessInfo).taxId
                    : (billingData.invoice as IBusinessInfo).taxId}
                </Typography.Text>
              </Col>
              <Col flex={1} className="p-addNewPackage_buyerInfo_col">
                <Typography.Text fontweight="600" extendClasses="p-addNewPackage_billingType_formLabel p-home_demo_formLabel fs-14x18">
                  {billingData.invoiceType === 'business'
                    ? 'Company Name' : 'Full Name'}
                </Typography.Text>
                <Typography.Text extendClasses="p-addNewPackage_text">
                  {billingData.invoiceType === 'business' ? (billingData.invoice as IBusinessInfo).companyName
                    : (billingData.invoice as IBusinessInfo).companyName}
                </Typography.Text>
              </Col>
            </Row>
            <Row>
              <Col flex={1} className="p-addNewPackage_buyerInfo_col">
                <Typography.Text fontweight="600" extendClasses="p-addNewPackage_billingType_formLabel p-home_demo_formLabel fs-14x18">Full Address</Typography.Text>
                <Typography.Text extendClasses="p-addNewPackage_text">{billingData.invoice.fullAddress}</Typography.Text>
              </Col>
            </Row>
            <Row>
              <Col>
                <Typography.Text fontweight="600" extendClasses="p-addNewPackage_billingType_formLabel p-home_demo_formLabel fs-14x18">Invoice Email</Typography.Text>
                <div className="p-addNewPackage_textTagWrap">
                  {billingData.invoice.invoiceEmail?.map((item) => (
                    <Typography.Text extendClasses="p-addNewPackage_textTag">{item}</Typography.Text>
                  ))}
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </div>

      <div className="p-addNewPackage_confirmation_payment p-addNewPackage_productWrap">
        <div>
          <div className="p-addNewPackage_padding24">
            <Typography.Text fontweight="600" extendClasses=" p-home_demo_formLabel fs-20x24">{cardData && cardData.length > 0 ? 'Payment method' : 'Support payment method'}</Typography.Text>
          </div>

          <div className="p-addNewPackage_confirmation_paymentMethods p-addNewPackage_padding24">
            {
              mainCard
              && (
                <div>
                  <Flex gap={4} align="center">
                    <Radio checked={paymentMethod === 'card'} onClick={() => setMethod('card')} size={20} />
                    <Typography.Text fontweight="600" extendClasses="fs-14x20">Your card</Typography.Text>
                  </Flex>
                  {
                    paymentMethod === 'card'
                    && (
                      <>
                        <div className="t-payment_card_imgCardWrap">
                          <Image
                            imgSrc={usedCard === 'main' ? mainCardImg : backupCardImg}
                            alt="primary-card"
                            ratio={310 / 171}
                            extendClasses="p-paymentMethod_cardInfo_cardImg"
                          />
                          {usedCard === 'main'

                            ? (
                              <div className="t-overview_cardInfo  t-overview_cardInfo-padding16">
                                <Image
                                  imgSrc={
                                    mainCard?.cardInfoData?.paymentMethod === 'MASTERCARD'
                                      ? masterCard
                                      : visa
                                  }
                                  ratio={50 / 32}
                                  alt="card-brand"
                                  extendClasses="t-overview_cardInfo_cardBrannd"
                                />
                                <Typography.Text
                                  extendClasses="fs-28x33 t-overview_cardInfo_cardNumber"
                                  fontweight="600"
                                >
                                  {mainCard?.cardInfoData?.cardNumber}
                                </Typography.Text>

                                <Flex justify="space-between">
                                  <Flex vertical>
                                    <Typography.Text>Card holder</Typography.Text>
                                    <Typography.Text
                                      extendClasses="fs-16x24 t-overview_cardInfo_cardNumber"
                                      fontweight="600"
                                    >
                                      {mainCard?.cardInfoData?.cardHolderName}
                                    </Typography.Text>
                                  </Flex>
                                  <Flex vertical align="flex-end">
                                    <Typography.Text>Expires </Typography.Text>
                                    <Typography.Text
                                      extendClasses="fs-16x24 t-overview_cardInfo_cardNumber"
                                      fontweight="600"
                                    >
                                      {`${mainCard?.cardInfoData?.cardExpireMonth}/${mainCard?.cardInfoData?.cardExpireYear}`}
                                    </Typography.Text>
                                  </Flex>
                                </Flex>
                              </div>
                            )
                            : (
                              <div className="t-overview_cardInfo  t-overview_cardInfo-padding16">
                                <Image
                                  imgSrc={
                                    backupCard?.cardInfoData?.paymentMethod === 'MASTERCARD'
                                      ? masterCard
                                      : visa
                                  }
                                  ratio={50 / 32}
                                  alt="card-brand"
                                  extendClasses="t-overview_cardInfo_cardBrannd"
                                />
                                <Typography.Text
                                  extendClasses="fs-28x33 t-overview_cardInfo_cardNumber"
                                  fontweight="600"
                                >
                                  {backupCard?.cardInfoData?.cardNumber}
                                </Typography.Text>

                                <Flex justify="space-between">
                                  <Flex vertical>
                                    <Typography.Text>Card holder</Typography.Text>
                                    <Typography.Text
                                      extendClasses="fs-16x24 t-overview_cardInfo_cardNumber"
                                      fontweight="600"
                                    >
                                      {backupCard?.cardInfoData?.cardHolderName}
                                    </Typography.Text>
                                  </Flex>
                                  <Flex vertical align="flex-end">
                                    <Typography.Text>Expires </Typography.Text>
                                    <Typography.Text
                                      extendClasses="fs-16x24 t-overview_cardInfo_cardNumber"
                                      fontweight="600"
                                    >
                                      {`${backupCard?.cardInfoData?.cardExpireMonth}/${backupCard?.cardInfoData?.cardExpireYear}`}
                                    </Typography.Text>
                                  </Flex>
                                </Flex>
                              </div>
                            )}

                        </div>

                        {backupCard && (
                          <Button
                            onClick={() => {
                              if (usedCard === 'main') {
                                setUsedCard('backup');
                              } else {
                                setUsedCard('main');
                              }
                            }}
                            extendClasses="p-addNewPackage_cardInfo_btnTxt"
                            variant="text"
                          >
                            {usedCard === 'main' ? 'Use backup card' : 'Use primary card'}
                          </Button>
                        )}

                      </>
                    )
                  }
                </div>
              )
            }

            <div className={`p-paymentMethod_cardInfo_others ${cardData && cardData.length === 0 ? 'p-addNewPackage_mt0' : ''}`}>
              {
                cardData && cardData?.length > 0
                && (
                  <Flex gap={4} align="center">
                    <Radio checked={paymentMethod === 'other'} onClick={() => setMethod('other')} size={20} />
                    <Typography.Text fontweight="600" extendClasses="fs-14x20">Others</Typography.Text>
                  </Flex>
                )
              }

              {
                paymentMethod === 'other' && (
                  <ul className="p-addNewPackage_confirmation_listPayment">
                    {(billingType === 'one-time' ? paymentMethodsOnetime : paymentMethodsSubscription)
                      .map((item) => (
                        <li
                          className="p-addNewPackage_confirmation_radio"
                          key={item}
                        >
                          <Typography.Text extendClasses="p-addNewPackage_text fs-14x18">{item}</Typography.Text>
                        </li>
                      ))}
                  </ul>
                )
              }

            </div>
          </div>

          <div className="p-addNewPackage_padding24">
            <div>
              <Row justify="space-between">
                <Typography.Text extendClasses="fs-16x18">Total</Typography.Text>
                <Typography.Text fontweight="600" extendClasses="p-addNewPackage_packageCard_amount fs-18x21">
                  {total.toLocaleString('vi')}
                  {' '}
                  VNĐ
                </Typography.Text>
              </Row>
              <div className="p-addNewPackage_confirmation_agreement">
                <Checkbox onChange={() => setChecked(!checked)} />
                <Typography.Text extendClasses="p-addNewPackage_confirmation_agreementText fs-14x18">
                  I agree to Ask2Auto
                  {' '}
                  <span className="p-addNewPackage_confirmation_link" onClick={() => window.open('/privacy-policy', '_blank')}>privacy policy</span>
                  {' '}
                  and
                  {' '}
                  <span className="p-addNewPackage_confirmation_link" onClick={() => window.open('/terms-and-conditions', '_blank')}>terms</span>
                </Typography.Text>
              </div>
              <Button
                onClick={() => (billingType === 'one-time' || paymentMethod === 'card' ? handleCheckout() : setOpenModal(true))}
                disabled={!checked}
                extendClasses={mapModifiers('fButton fButton-primary p-addNewPackage_confirmation_button', !checked ? 'disabled' : 'active')}
              >
                {paymentMethod === 'card' ? 'Pay' : 'Select Payment Method'}
              </Button>
            </div>
            <div className="p-addNewPackage_confirmation_support">
              <Typography.Text extendClasses="p-addNewPackage_confirmation_supportText">Supported by payment gateway</Typography.Text>
              <div className="p-addNewPackage_confirmation_supportImg">
                <Image extendClasses="p-addNewPackage_confirmation_extend" imgSrc={nganLuongImg} alt="nganLuongImg" ratio={394 / 87} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={openModal}
        className="p-addNewPackage_modal"
        width={800}
        title="Notice"
        closeIcon
        cancelButton={{
          handleClick: () => {
            setOpenModal(false);
          },
        }}
        okButton={{
          handleClick: () => {
            setOpenModal(false);
          },
        }}
      >
        <div className="p-addNewPackage_modal_content">
          <Divider style={{ margin: '8px 0 16px 0' }} />
          <Image extendClasses="p-addNewPackage_modal_image" imgSrc={imgCard} alt="demoImg" ratio={1447 / 680} />
          <Typography.Text extendClasses="p-addNewPackage_modal_description f-14x18">
            To enable automatic billing, you must agree to save
            your card information with our payment partner
          </Typography.Text>
          <Divider style={{ margin: '12px 0' }} />
          <div className="p-addNewPackage_modal_buttonWrap">
            <Button onClick={handleCheckout} variant="primary" extendClasses="p-addNewPackage_modal_button">I understand</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Confirmation;
